import { z } from 'zod';
import { BUILD_HUB_TITLE_MAX_LENGTH } from '../configs/constants';

// ============================================================================
// CORE VALIDATION SCHEMAS
// ============================================================================

/**
 * Project title validation schema
 * Used in: NewProjectStep, useBuildHubForm
 */
export const ProjectTitleSchema = z
    .string()
    .min(1, 'Project title is required')
    .max(
        BUILD_HUB_TITLE_MAX_LENGTH,
        'Project title must be 60 characters or less'
    )
    .trim();

/**
 * Project scope validation
 */
export const ProjectScopeSchema = z.enum(
    ['new', 'renovation', 'landscaping', 'interior'],
    {
        errorMap: () => ({ message: 'Please select a valid project scope' }),
    }
);

/**
 * Location validation with detailed place structure
 */
export const LocationDataSchema = z
    .object({
        place: z.object({
            address1: z.string().optional(),
            address2: z.string().optional(),
            addressID: z.string().optional(),
            city: z.string().optional(),
            country: z.string().optional(),
            depth: z.number().optional(),
            district: z.string().optional(),
            fullAddress: z.string().optional(),
            latitude: z.number().optional(),
            longitude: z.number().optional(),
            parentID: z.number().optional(),
            placeName: z.string().optional(),
            placeType: z.string().optional(),
            postCode: z.string().optional(),
            region: z.string().optional(),
            serviceableAreaID: z.number().optional(),
            state: z.string().optional(),
            suburb: z.string().optional(),
        }),
        formattedAddress: z.string().min(1, 'Address is required'),
    })
    .refine((data) => data.formattedAddress.trim().length > 0, {
        message: 'Please provide a valid location',
        path: ['formattedAddress'],
    });

/**
 * Timeline validation with business rules
 */
export const TimelineDataSchema = z
    .object({
        startDate: z.string().optional(),
        endDate: z.string().optional(),
        isNotSure: z.boolean().default(false),
    })
    .refine(
        (data) => {
            // At least one field should be provided
            return data.startDate || data.endDate || data.isNotSure;
        },
        {
            message: 'Please provide timeline information',
        }
    )
    .refine(
        (data) => {
            // If both dates provided, start should be before end
            if (data.startDate && data.endDate) {
                return new Date(data.startDate) <= new Date(data.endDate);
            }
            return true;
        },
        {
            message: 'Start date must be before end date',
            path: ['endDate'],
        }
    );

/**
 * Budget validation with custom amount format checking
 */
export const BudgetDataSchema = z.object({
    min: z.preprocess(
        (val) => (typeof val === 'number' && !isNaN(val) ? val : undefined),
        z.number().min(0).optional()
    ),
    max: z.preprocess(
        (val) => (typeof val === 'number' && !isNaN(val) ? val : undefined),
        z.number().min(0).optional()
    ),
});

/**
 * Complete form schema for final submission
 */
export const BuildHubFormDataSchema = z.object({
    title: ProjectTitleSchema,
    scope: ProjectScopeSchema.optional(),
    location: LocationDataSchema.optional(),
    timeline: TimelineDataSchema.optional(),
    budget: BudgetDataSchema.optional(),
});

/**
 * API Response validation schemas
 */
export const CreateBuildHubResponseSchema = z.object({
    success: z.boolean(),
    buildHub: z
        .object({
            id: z.string(),
        })
        .optional(),
    errors: z.record(z.string()).optional(),
});

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

/**
 * Step ID to schema mapping for consolidated validation
 */
export const STEP_VALIDATION_MAP = {
    'new-project': ProjectTitleSchema,
    'project-scope': ProjectScopeSchema,
    'project-location': LocationDataSchema,
    'project-timeline': TimelineDataSchema,
    'project-budget': BudgetDataSchema,
} as const;

/**
 * Validation result interface
 */
export interface ValidationResult {
    isValid: boolean;
    errors: Record<string, string>;
}

/**
 * Converts Zod errors to user-friendly format
 */
export function formatZodErrors(error: z.ZodError): Record<string, string> {
    const errors: Record<string, string> = {};
    error.errors.forEach((err) => {
        const path = err.path.join('.');
        errors[path] = err.message;
    });
    return errors;
}

/**
 * Validates data for a specific step
 * This is the SINGLE validation function used throughout the app
 */
export function validateStepData(
    stepId: string,
    data: unknown
): ValidationResult {
    try {
        const schema =
            STEP_VALIDATION_MAP[stepId as keyof typeof STEP_VALIDATION_MAP];
        if (!schema) {
            return { isValid: true, errors: {} };
        }

        // For optional steps, allow null/undefined data
        if (!data && stepId !== 'new-project') {
            return { isValid: true, errors: {} };
        }

        schema.parse(data);
        return { isValid: true, errors: {} };
    } catch (error) {
        if (error instanceof z.ZodError) {
            return { isValid: false, errors: formatZodErrors(error) };
        }
        return { isValid: false, errors: { general: 'Validation failed' } };
    }
}

/**
 * Validates complete form data for final submission
 */
export function validateCompleteForm(data: unknown): ValidationResult {
    try {
        BuildHubFormDataSchema.parse(data);
        return { isValid: true, errors: {} };
    } catch (error) {
        if (error instanceof z.ZodError) {
            return { isValid: false, errors: formatZodErrors(error) };
        }
        return { isValid: false, errors: { general: 'Validation failed' } };
    }
}
